import { makeAutoObservable } from 'mobx';
import type RootStore from '../RootStore';

export class ShareStore {
    rootStore: RootStore;
    isSupported = 'share' in navigator;
    canShare = false;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false,
        });
        this.rootStore = rootStore;

        if (this.isSupported) {
            this.canShare = true;
        }
    }

    async share(data: ShareData): Promise<void> {
        if (!this.isSupported) {
            // Fallback to copying URL
            if (data.url) {
                const text = `${data.title || ''}\n${data.text || ''}\n${data.url}`.trim();
                await navigator.clipboard.writeText(text);

                if (this.rootStore.notificationStore) {
                    this.rootStore.notificationStore.addNotification({
                        message: 'Link copied to clipboard',
                        options: {
                            variant: 'success',
                            autoHideduration: 2000,
                        },
                    });
                }
            }
            return;
        }

        try {
            await navigator.share(data);
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                throw error;
            }
        }
    }

    canShareFiles() {
        if (!this.isSupported) return false;

        // Test if file sharing is supported
        try {
            const testFile = new File(['test'], 'test.txt', { type: 'text/plain' });
            return navigator.canShare({ files: [testFile] });
        } catch {
            return false;
        }
    }

    async shareFiles(files: File[], data?: Partial<ShareData>): Promise<void> {
        if (!this.canShareFiles()) {
            throw new Error('File sharing not supported');
        }

        await navigator.share({
            ...data,
            files,
        });
    }
}